import {configureStore} from '@reduxjs/toolkit';

import {BankAccountsReducer} from '../features/bank-accounts/reducers';
import {CardsReducer} from '../features/cards/reducers';
import {CustomerInvoiceItemsReducer} from '../features/customer-invoice-items/reducers';
import {CustomersReducer} from '../features/customers/reducers';
import {InvoiceItemsReducer} from '../features/invoice-items/reducers';
import {InvoicesReducer} from '../features/invoices/reducers';
import {PaymentIntentsReducer} from '../features/payment-intents/reducers';
import {PaymentMethodsReducer} from '../features/payment-methods/reducers';
import {PricesReducer} from '../features/prices/reducers';
import {ProductsReducer} from '../features/products/reducers';
import {ReportRunsReducer} from '../features/report-runs/reducers';
import {ReportTypesReducer} from '../features/report-types/reducers';
import {SetupIntentsReducer} from '../features/setup-intents/reducers';
import {SubscriptionItemsReducer} from '../features/subscription-items/reducers';
import {SubscriptionsReducer} from '../features/subscriptions/reducers';

// Row selection state (FNP-951). Records are React Query's job; selection is
// Redux's.
//
// ⚠️ ONE ENTRY PER MODULE, and the key MUST equal the `entityKey` passed to
// SelectionSliceFor in that module's reducers/index.ts. A mismatch compiles
// cleanly and reads `undefined` at runtime — `state['prices']` against a slice
// registered as `price` is not a type error, because the selector is written
// against RootState, which is inferred FROM this map. The guard is
// src/store/tests/storeKeys.test.ts, which asserts every registered key against
// the slice name the reducer actually answers to.
//
// All 15 feature modules are registered (FNP-918). A module that later gains a
// sibling slice (filter, visibility) changes only its own reducers/index.ts —
// nothing here.
const reducers = {
    'bank-accounts': BankAccountsReducer,
    'cards': CardsReducer,
    'customer-invoice-items': CustomerInvoiceItemsReducer,
    'customers': CustomersReducer,
    'invoice-items': InvoiceItemsReducer,
    'invoices': InvoicesReducer,
    'payment-intents': PaymentIntentsReducer,
    'payment-methods': PaymentMethodsReducer,
    'prices': PricesReducer,
    'products': ProductsReducer,
    'report-runs': ReportRunsReducer,
    'report-types': ReportTypesReducer,
    'setup-intents': SetupIntentsReducer,
    'subscription-items': SubscriptionItemsReducer,
    'subscriptions': SubscriptionsReducer,
};

/**
 * Builds a store instance.
 *
 * Exported as a FACTORY, not only as the singleton below, because the store
 * holds mutable selection state: a test that selects two rows leaves them
 * selected for every test that runs after it in the same file. Tests call this
 * per render; the app uses the singleton.
 */
export const createAppStore = () => configureStore({
    reducer: reducers,
    // Selection stores whole records, which carry non-serializable values in
    // some modules; the check is noisy rather than useful for this store's one
    // job. Matches console-app.
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }),
});

export const store = createAppStore();

// Inferred from the store itself, so adding a module's reducer above widens
// RootState with no further edits here.
export type AppStore = ReturnType<typeof createAppStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
